import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  History,
  Loader2,
  Pencil,
  PenLine,
  FileDown,
  Trash2,
  Plus,
  User,
  RefreshCw,
  X,
} from "lucide-react";

const ACTION_META = {
  create: { icon: Plus, color: "text-emerald-700", bg: "bg-emerald-100 border-emerald-200", label: "Creazione" },
  patch_step: { icon: Pencil, color: "text-sky-700", bg: "bg-sky-100 border-sky-200", label: "Modifica step" },
  sign: { icon: PenLine, color: "text-violet-700", bg: "bg-violet-100 border-violet-200", label: "Firma" },
  generate: { icon: FileDown, color: "text-amber-700", bg: "bg-amber-100 border-amber-200", label: "Generazione Word" },
  delete: { icon: Trash2, color: "text-rose-700", bg: "bg-rose-100 border-rose-200", label: "Eliminazione" },
};

const STEP_LABELS = {
  step1_dati_generali: "1. Dati generali",
  step2_classificazione: "2. Classificazione",
  step3_territoriale: "3. Territoriale",
  step4_mansioni: "4. Mansioni",
  step5_pericoli: "5. Pericoli",
  attrezzature: "6. Attrezzature",
  sostanze: "7. Sostanze",
  step7bis_vdt: "7bis. VDT",
  step7ter_incendio: "7ter. Incendio",
  step7quater_sorveglianza: "7quater. Sorveglianza",
  step7quinquies_madri: "7quinquies. Madri",
  step7sexies_minorenni: "7sexies. Minorenni",
  step7septies_apprendisti: "7septies. Apprendisti",
  step7octies_stranieri: "7octies. Stranieri",
  step7nonies_estero: "7nonies. Estero",
  step7decies_segnaletica: "7decies. Segnaletica",
  piano_miglioramento: "8. Piano miglioramento",
  firme: "9. Firme",
};

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * DVRAuditPanel — Timeline delle modifiche al DVR (audit trail).
 * Compliance: art. 28 D.Lgs. 81/08 richiede tracciabilità della valutazione dei rischi.
 * Ogni azione (create, patch_step, sign, generate, delete) è tracciata con utente, ruolo,
 * timestamp e riepilogo del cambiamento (# elementi aggiunti/rimossi/editati).
 */
export default function DVRAuditPanel({ dvrId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dvr/documents/${dvrId}/audit-log`);
      setRows(r.data || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Errore caricamento log");
    } finally {
      setLoading(false);
    }
  }, [dvrId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[520px] bg-background border-l border-border shadow-2xl z-50 flex flex-col" data-testid="dvr-audit-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <History className="w-4 h-4" /> Cronologia modifiche
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Compliance art. 28 D.Lgs. 81/08 — tracciabilità della valutazione dei rischi
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="dvr-audit-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="dvr-audit-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Caricamento…
          </div>
        )}
        {error && (
          <div className="border border-rose-200 bg-rose-50 text-rose-800 text-sm p-3 rounded">{error}</div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-6 text-center">
            Nessuna modifica registrata.
          </div>
        )}
        {!loading &&
          rows.map((r) => {
            const meta = ACTION_META[r.action] || {
              icon: Pencil,
              color: "text-slate-700",
              bg: "bg-slate-100 border-slate-200",
              label: r.action,
            };
            const Icon = meta.icon;
            return (
              <div key={r.id} className="border border-border rounded-md p-3 bg-card" data-testid={`audit-row-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${meta.bg} flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{meta.label}</span>
                      {r.step_key && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                          {STEP_LABELS[r.step_key] || r.step_key}
                        </span>
                      )}
                    </div>
                    <div className="text-sm mt-1">{r.summary || "—"}</div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1.5">
                      <User className="w-3 h-3" />
                      <span>{r.user_email || "—"}</span>
                      {r.user_role && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{r.user_role}</span>
                      )}
                      <span className="ml-auto">{fmtWhen(r.timestamp)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground text-center">
        {rows.length} modifica{rows.length === 1 ? "" : "he"} tracciata{rows.length === 1 ? "" : "e"}
      </div>
    </div>
  );
}
